import { Media, NewMedia, UpdateMedia } from "./CmsMediaType";
import { CmsTag, CmsTagKey } from "./CmsTagType";
import { ProviderKey } from "./CmsProviderType";
import {
  ContentRights,
  NewContentRights,
  UpdateContentRights,
} from "./CmsContentRightsType";
import {
  PaginationMetadata,
  PaginationQuery,
  DateTime,
} from "./CmsCommonTypes";
import { Genre, GenreKey } from "./CmsGenreType";
import { LocalizedImage, NewLocalizedImage } from "./CmsImageType";

export interface ParentalRatingKey {
  assetId: string;
  country: string;
}

export interface ParentalRating {
  country: string;
  ratingValue: number;
}

export interface Rating {
  source: string;
  value: string;
}

export interface ExternalLink {
  name: string;
  url: string;
}

export enum AssetType {
  Movie = "movie",
  Series = "series",
  Season = "season",
  Episode = "episode",
  Channel = "channel",
}

export enum SourceType {
  vod = "vod",
  live = "live",
  catchup = "catchup",
  deeplink = "deeplink",
}

export type ContributorRole = string;

export interface Contributor {
  role: ContributorRole;
  fullName: string;
}

export interface AlternativeId {
  source: string;
  alternativeId: string;
}

export interface AssetContributor {
  role: ContributorRole;
  contributor: Contributor;
}

export interface LocalizedAssetInfo {
  language: string;
  title: string;
  synopsisLong: string;
  synopsisMedium: string;
  synopsisShort: string;
}

export interface LocalizedAssetInfoKey {
  assetId: string;
  language: string;
}

export interface AssetKey {
  assetId: string;
}

export type AssetGenre = Omit<Genre, "alternativeIds">;

export type EpgEvent = {
  epgEventId: number;
  type: string;
  externalId: string;
  programAssetId: string;
  channelAssetId: string;
  startScheduled: DateTime;
  endScheduled: DateTime;
  startActual?: DateTime;
  endActual?: DateTime;
  createdAt: DateTime;
  updatedAt: DateTime;
};

export interface CmsAsset {
  assetId: string;
  externalId: string;
  type: AssetType;
  sourceType: SourceType;
  provider: {
    providerId: number;
    name: string;
  };
  originalTitle: string;
  localizedInfo?: LocalizedAssetInfo[];
  medias?: Media[];
  images?: LocalizedImage[];
  tags?: CmsTag[];
  parentalRatings?: ParentalRating[];
  contentRights?: ContentRights[];
  genres?: AssetGenre[];
  keywords?: string[];
  contributors?: Contributor[];
  alternativeIds?: AlternativeId[];
  createdAt: DateTime;
  updatedAt: DateTime;
  programEpgEvents?: Array<EpgEvent>;
  channelEpgEvents?: Array<EpgEvent>;

  // VOD-specific fields
  seasonNumber?: number;
  episodeNumber?: number;
  seriesId?: string;
  seasonId?: string;
  countryOfOrigin?: string;
  copyrightInfo?: string;
  originalLanguage?: string;
  productionYear?: number;
  releaseYear?: number;
  studio?: string;
  eidrId?: string;
  duration?: number;
}

export type UpdateAsset = Partial<
  Omit<
    CmsAsset,
    | "assetId"
    | "createdAt"
    | "updatedAt"
    | "medias"
    | "images"
    | "contentRights"
    | "tags"
    | "provider"
    | "genres"
    | "epgEvents"
  >
> & {
  images?: Array<LocalizedImage | NewLocalizedImage>;
  contentRights?: Array<NewContentRights | UpdateContentRights>;
  tags?: CmsTagKey[];
  genres?: GenreKey[];
  provider?: ProviderKey;
  medias?: Array<NewMedia | UpdateMedia>;
};

export type NewAsset = Omit<
  CmsAsset,
  | "assetId"
  | "createdAt"
  | "updatedAt"
  | "localizedInfo"
  | "medias"
  | "images"
  | "contentRights"
  | "tags"
  | "provider"
  | "genres"
  | "epgEvents"
> & {
  localizedInfo?: LocalizedAssetInfo[];
  medias?: NewMedia[];
  images?: NewLocalizedImage[];
  contentRights?: NewContentRights[];
  tags?: CmsTagKey[];
  genres?: GenreKey[];
  provider: ProviderKey;
};

export interface AssetIdTagId {
  assetId: string;
  tagId: number;
}

export type AssetIdProviderId = AssetKey & ProviderKey;

export interface AssetsPage {
  pagination: PaginationMetadata;
  assets: CmsAsset[];
}

export type AssetQueryInclude =
  | "none"
  | "all"
  | "provider"
  | "localizedInfo"
  | "medias"
  | "images"
  | "tags"
  | "parentalRatings"
  | "contentRights";

export interface AssetQueryIncludes {
  /** Which additional asset info to include in the response. Zero or more of none, all, provider, localizedInfo, medias, images, tags, parentalRatings, contentRights. Defaults to all */
  include?: AssetQueryInclude[];
}
export type AssetFilter = {
  assetId?: string[] | undefined;
  externalId?: string | undefined;
  type?: ("movie" | "series" | "season" | "episode" | "channel")[] | undefined;
  sourceType?: SourceType[] | undefined;
  providerId?: string[] | undefined;
  originalTitle?: string | undefined;
  title?: string | undefined;
  tagId?: string[] | undefined;
  genreId?: string[] | undefined;
  seriesId?: string | undefined;
  seasonId?: string | undefined;
  titleLang?: string | undefined;
  availableInCountry?: string | undefined;
  keyword?: string[] | undefined;
  contributor?: string[] | undefined;
};

export type CatchupFilter = {
  channelAssetIds: string[];
  epgFrom?: Date;
  epgTo?: Date;
};

export type CatchupAssetQuery = PaginationQuery &
  AssetQueryIncludes &
  CatchupFilter &
  Pick<AssetFilter, "type" | "sourceType" | "genreId" | "providerId" | "tagId">;

export type AssetQuery = PaginationQuery & AssetFilter & AssetQueryIncludes;
export type EntitledAssetQuery = AssetQuery & {
  unitId: string;
};
